/**
 * Frontend Input Validation Utilities
 *
 * @description Client-side validation for forms and user inputs
 * @version 1.0.0
 * @created 2025-11-13
 *
 * Purpose:
 * - Provide real-time validation feedback to users
 * - Prevent invalid data from reaching backend
 * - Improve UX with clear, helpful error messages
 * - Work with Mantine form validation
 */

// ============================================================================
// VALIDATION CONSTANTS
// ============================================================================

/**
 * Consumption bounds (kWh)
 */
export const CONSUMPTION_BOUNDS = {
  min: 0,
  max: 10000,
  typicalMin: 50,
  typicalMax: 2000,
} as const;

/**
 * Parameter value bounds (PLN)
 */
export const PARAMETER_VALUE_BOUNDS = {
  min: 0,
  max: 100000,
  typicalMax: 10000,
} as const;

/**
 * Month index bounds
 */
export const MONTH_INDEX_BOUNDS = {
  min: 0,
  max: 11,
} as const;

/**
 * Year bounds
 */
export const YEAR_BOUNDS = {
  min: 2020,
  max: 2030,
} as const;

// ============================================================================
// ERROR MESSAGES (Polish)
// ============================================================================

export const ERROR_MESSAGES = {
  // Consumption errors
  CONSUMPTION_REQUIRED: 'Zużycie jest wymagane',
  CONSUMPTION_NEGATIVE: 'Zużycie nie może być ujemne',
  CONSUMPTION_TOO_HIGH: 'Zużycie jest zbyt wysokie (max 10,000 kWh)',
  CONSUMPTION_NOT_A_NUMBER: 'Zużycie musi być liczbą',
  CONSUMPTION_WARNING_LOW: 'Zużycie jest bardzo niskie (poniżej 50 kWh)',
  CONSUMPTION_WARNING_HIGH: 'Zużycie jest bardzo wysokie (powyżej 2,000 kWh)',

  // Parameter errors
  PARAMETER_REQUIRED: 'Wartość parametru jest wymagana',
  PARAMETER_NEGATIVE: 'Wartość parametru nie może być ujemna',
  PARAMETER_TOO_HIGH: 'Wartość parametru jest zbyt wysoka (max 100,000 PLN)',
  PARAMETER_NOT_A_NUMBER: 'Wartość parametru musi być liczbą',

  // Tariff errors
  TARIFF_REQUIRED: 'Taryfa jest wymagana',
  TARIFF_INVALID: 'Nieprawidłowa taryfa (dozwolone: G11, G12, G12w, G12as, G13, G13s)',

  // Pattern errors
  PATTERN_REQUIRED: 'Wzorzec godzinowy jest wymagany',
  PATTERN_INVALID: 'Nieprawidłowy wzorzec godzinowy',

  // Month errors
  MONTH_INDEX_INVALID: 'Nieprawidłowy indeks miesiąca (0-11)',

  // Year errors
  YEAR_REQUIRED: 'Rok jest wymagany',
  YEAR_INVALID: 'Nieprawidłowy rok (2020-2030)',
} as const;

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate consumption value
 * @returns Error message or null if valid
 */
export function validateConsumption(value: unknown): string | null {
  // Check if value exists
  if (value === null || value === undefined || value === '') {
    return ERROR_MESSAGES.CONSUMPTION_REQUIRED;
  }

  // Convert to number
  const num = Number(value);

  // Check if NaN
  if (Number.isNaN(num)) {
    return ERROR_MESSAGES.CONSUMPTION_NOT_A_NUMBER;
  }

  // Check if negative
  if (num < CONSUMPTION_BOUNDS.min) {
    return ERROR_MESSAGES.CONSUMPTION_NEGATIVE;
  }

  // Check if too high
  if (num > CONSUMPTION_BOUNDS.max) {
    return ERROR_MESSAGES.CONSUMPTION_TOO_HIGH;
  }

  return null; // Valid
}

/**
 * Validate consumption with warnings
 * @returns Error message, warning message, or null if valid
 */
export function validateConsumptionWithWarnings(value: unknown): { error: string | null; warning: string | null } {
  const error = validateConsumption(value);

  if (error) {
    return { error, warning: null };
  }

  const num = Number(value);

  // Check for warnings
  if (num > 0 && num < CONSUMPTION_BOUNDS.typicalMin) {
    return { error: null, warning: ERROR_MESSAGES.CONSUMPTION_WARNING_LOW };
  }

  if (num > CONSUMPTION_BOUNDS.typicalMax) {
    return { error: null, warning: ERROR_MESSAGES.CONSUMPTION_WARNING_HIGH };
  }

  return { error: null, warning: null }; // Valid, no warnings
}

/**
 * Validate parameter value
 * @returns Error message or null if valid
 */
export function validateParameterValue(value: unknown, _parameterName?: string): string | null {
  // Check if value exists
  if (value === null || value === undefined || value === '') {
    return ERROR_MESSAGES.PARAMETER_REQUIRED;
  }

  // Convert to number
  const num = Number(value);

  // Check if NaN
  if (Number.isNaN(num)) {
    return ERROR_MESSAGES.PARAMETER_NOT_A_NUMBER;
  }

  // Check if negative
  if (num < PARAMETER_VALUE_BOUNDS.min) {
    return ERROR_MESSAGES.PARAMETER_NEGATIVE;
  }

  // Check if too high
  if (num > PARAMETER_VALUE_BOUNDS.max) {
    return ERROR_MESSAGES.PARAMETER_TOO_HIGH;
  }

  return null; // Valid
}

/**
 * Validate tariff type
 * @returns Error message or null if valid
 */
export function validateTariff(value: unknown): string | null {
  if (!value) {
    return ERROR_MESSAGES.TARIFF_REQUIRED;
  }

  const validTariffs = ['G11', 'G12', 'G12w', 'G12as', 'G13', 'G13s'];
  if (!validTariffs.includes(String(value))) {
    return ERROR_MESSAGES.TARIFF_INVALID;
  }

  return null; // Valid
}

/**
 * Validate hourly pattern type
 * @returns Error message or null if valid
 */
export function validateHourlyPattern(value: unknown): string | null {
  if (!value) {
    return ERROR_MESSAGES.PATTERN_REQUIRED;
  }

  const validPatterns = [
    'UNIFORM',
    'MORNINGS_EVENINGS',
    'WORK_FROM_HOME',
    'NIGHT_ELECTRIC',
    'FROM_CSV_IMPORT',
  ];

  if (!validPatterns.includes(String(value))) {
    return ERROR_MESSAGES.PATTERN_INVALID;
  }

  return null; // Valid
}

/**
 * Validate month index
 * @returns Error message or null if valid
 */
export function validateMonthIndex(value: unknown): string | null {
  const num = Number(value);

  if (Number.isNaN(num) || num < MONTH_INDEX_BOUNDS.min || num > MONTH_INDEX_BOUNDS.max) {
    return ERROR_MESSAGES.MONTH_INDEX_INVALID;
  }

  return null; // Valid
}

/**
 * Validate year
 * @returns Error message or null if valid
 */
export function validateYear(value: unknown): string | null {
  if (!value) {
    return ERROR_MESSAGES.YEAR_REQUIRED;
  }

  const num = Number(value);

  if (Number.isNaN(num) || num < YEAR_BOUNDS.min || num > YEAR_BOUNDS.max) {
    return ERROR_MESSAGES.YEAR_INVALID;
  }

  return null; // Valid
}

// ============================================================================
// MANTINE FORM VALIDATION HELPERS
// ============================================================================

/**
 * Create Mantine form validation rules for ProCalculator
 * Use with Mantine's useForm hook
 */
export function createProCalculatorValidationRules() {
  return {
    // Validate calculation year
    calculationYear: (value: unknown) => validateYear(value),

    // Validate months array
    'months.*.consumption': (value: unknown) => validateConsumption(value),
    'months.*.tariff': (value: unknown) => validateTariff(value),
    'months.*.hourlyPattern': (value: unknown) => validateHourlyPattern(value),

    // Validate all invoice parameters
    'months.*.parameters.energyElectricCost.valueFinal': (value: unknown) => validateParameterValue(value, 'energyElectricCost'),
    'months.*.parameters.handlingFee.valueFinal': (value: unknown) => validateParameterValue(value, 'handlingFee'),
    'months.*.parameters.networkFixedFee.valueFinal': (value: unknown) => validateParameterValue(value, 'networkFixedFee'),
    'months.*.parameters.transitionFee.valueFinal': (value: unknown) => validateParameterValue(value, 'transitionFee'),
    'months.*.parameters.qualityFee.valueFinal': (value: unknown) => validateParameterValue(value, 'qualityFee'),
    'months.*.parameters.networkVariableFee.valueFinal': (value: unknown) => validateParameterValue(value, 'networkVariableFee'),
    'months.*.parameters.ozeFee.valueFinal': (value: unknown) => validateParameterValue(value, 'ozeFee'),
    'months.*.parameters.cogenerationFee.valueFinal': (value: unknown) => validateParameterValue(value, 'cogenerationFee'),
    'months.*.parameters.subscriptionFee.valueFinal': (value: unknown) => validateParameterValue(value, 'subscriptionFee'),
    'months.*.parameters.powerFee.valueFinal': (value: unknown) => validateParameterValue(value, 'powerFee'),
  };
}

/**
 * Validate entire ProCalculatorFormValues object
 * Returns map of field -> error message
 */
export function validateProCalculatorForm(formValues: any): Record<string, string> {
  const errors: Record<string, string> = {};

  // Validate year
  const yearError = validateYear(formValues.calculationYear);
  if (yearError) {
    errors.calculationYear = yearError;
  }

  // Validate each month
  formValues.months?.forEach((month: any, index: number) => {
    // Validate consumption
    const consumptionError = validateConsumption(month.consumption);
    if (consumptionError) {
      errors[`months.${index}.consumption`] = consumptionError;
    }

    // Validate tariff
    const tariffError = validateTariff(month.tariff);
    if (tariffError) {
      errors[`months.${index}.tariff`] = tariffError;
    }

    // Validate hourly pattern
    const patternError = validateHourlyPattern(month.hourlyPattern);
    if (patternError) {
      errors[`months.${index}.hourlyPattern`] = patternError;
    }

    // Validate parameters (only if they exist)
    if (month.parameters) {
      Object.entries(month.parameters).forEach(([paramName, paramValue]: [string, any]) => {
        if (paramValue && typeof paramValue === 'object' && 'valueFinal' in paramValue) {
          const paramError = validateParameterValue(paramValue.valueFinal, paramName);
          if (paramError) {
            errors[`months.${index}.parameters.${paramName}.valueFinal`] = paramError;
          }
        }
      });
    }
  });

  return errors;
}

/**
 * Check if form has validation errors
 */
export function hasValidationErrors(errors: Record<string, string>): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(errors: Record<string, string>): string {
  const errorMessages = Object.entries(errors).map(([field, message]) => {
    // Format field name for display
    const fieldParts = field.split('.');
    const monthIndex = fieldParts[1] ? parseInt(fieldParts[1]) + 1 : null;
    const fieldName = fieldParts[fieldParts.length - 1];

    if (monthIndex) {
      return `Miesiąc ${monthIndex} - ${fieldName}: ${message}`;
    }
    return `${fieldName}: ${message}`;
  });

  return errorMessages.join('\n');
}
